import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class WaitForConfirmationRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.WaitForConfirmation', 'WaitForConfirmation');
  }

  protected validate({ args }: CanonicalValidateArgs): void {
    type WFCArgs = Extract<
      RcStep.Arguments,
      { argumentKind: 'WaitForConfirmation' }
    >;
    const a = args as WFCArgs;
    this.checkChoices(a);
    this.checkMessage(a);
  }

  /**
   * Validate choices when mode is 'choice'.
   * Throws ActionRequiredError if invalid (count, empties, duplicates).
   *
   * @throws {ActionRequiredError}
   */
  private checkChoices(
    args: Extract<RcStep.Arguments, { argumentKind: 'WaitForConfirmation' }>,
  ): void {
    if (args.mode !== 'choice') return;

    if ((args.choices?.length ?? 0) < 2) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'You must specify at least two choices.',
        fieldId: 'choices',
      });
    }

    if ((args.choices as string[]).some((c) => c.trim() === '')) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Options must not be empty. Delete empty options or fill them in.',
        fieldId: 'choices',
      });
    }

    if (new Set(args.choices).size !== args.choices.length) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'All options must be unique.',
        fieldId: 'choices',
      });
    }
  }

  /**
   * Ensure message is provided.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkMessage(
    args: Extract<RcStep.Arguments, { argumentKind: 'WaitForConfirmation' }>,
  ): void {
    if (!args.message) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Message is not configured',
        fieldId: 'message',
      });
    }
  }
}

export const WaitForConfirmationValidation: ValidationRule =
  new WaitForConfirmationRule().toValidationRule();
